import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api.js';
import { downloadPDF } from '../utils/pdfGenerator.js';
import { printHtmlDocument } from '../utils/printDocument.js';
import { createIsolatedPrintSurface } from '../utils/isolatedPrintSurface.js';
import { loadWorkshopPrintInfo } from '../utils/workshopPrintInfo.js';
import { useWhatsAppShare } from '../hooks/useWhatsAppShare.js';
import { assertTemplateComplete, findUnresolvedTemplateVariables, renderDocumentTemplate } from '../utils/documentTemplate.js';
import WhatsAppSharePreview from './WhatsAppSharePreview.js';
import DocumentOverlay from './DocumentOverlay.js';
import DocumentViewport from './DocumentViewport.js';
const QuickPrintDialog = ({ open, title = 'إجراء الطباعة', description = 'معاينة المستند قبل الطباعة أو الإرسال.', payloadBuilder, initialPhone = '', onClose, }) => {
    const [loading, setLoading] = useState(false);
    const [html, setHtml] = useState('');
    const [error, setError] = useState('');
    const [outputError, setOutputError] = useState('');
    const [phone, setPhone] = useState(initialPhone || '');
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [templateLoading, setTemplateLoading] = useState(false);
    const [templateSelectionReason, setTemplateSelectionReason] = useState('');
    const [zoom, setZoom] = useState(1);
    const [outputAction, setOutputAction] = useState('');
    const outputBusyRef = useRef(false);
    const [templateSheetOpen, setTemplateSheetOpen] = useState(false);


    const payloadBuilderRef = useRef(payloadBuilder);
    const generationRef = useRef(0);
    const lastPayloadRef = useRef(null);
    const { share, prepare: prepareShare, prepareAttachments, logEvent: logShareEvent, reset: resetShare } = useWhatsAppShare();
    useEffect(() => {
        payloadBuilderRef.current = payloadBuilder;
    }, [payloadBuilder]);
    const runWithTimeout = useCallback(async (promise, timeoutMs = 15000) => {
        let timer;
        try {
            return await Promise.race([promise, new Promise((_, reject) => {
                timer = setTimeout(() => reject(new Error('timeout')), timeoutMs);
            })]);
        } finally { clearTimeout(timer); }
    }, []);
    const currentDocType = useMemo(() => {
        const text = String(title || '');
        if (text.includes('تشخيص'))
            return 'diagnosis';
        if (text.includes('عرض'))
            return 'quote';
        if (text.includes('سند') || text.includes('زيارة') || text.includes('إيصال'))
            return 'receipt';
        return 'invoice';
    }, [title]);
    const filteredTemplates = useMemo(() => templates.filter((tpl) => (tpl.document_type || tpl.type || 'invoice') === currentDocType && tpl.file_type === 'html' && tpl.status === 'valid' && tpl.active), [currentDocType, templates]);
    const selectedTemplate = useMemo(() => filteredTemplates.find((tpl) => tpl.id === selectedTemplateId) || filteredTemplates[0] || null, [filteredTemplates, selectedTemplateId]);
    const loadTemplates = useCallback(async () => {
        setTemplateLoading(true);
        try {
            const response = await api.get('/document-templates');
            const rows = Array.isArray(response.data?.templates) ? response.data.templates : [];
            setTemplates(rows);
            const sameType = rows.filter((tpl) => (tpl.document_type || tpl.type || 'invoice') === currentDocType && tpl.file_type === 'html' && tpl.status === 'valid' && tpl.active);
            const preferred = sameType.find((tpl) => tpl.is_default || tpl.isActive) || sameType[0];
            setSelectedTemplateId(preferred?.id || '');
            if (!preferred) { setHtml(''); setError('لا يوجد قالب طباعة نشط لهذا النوع؛ راجع إدارة النماذج.'); }
        }
        catch (e) {
            setTemplates([]);
            setSelectedTemplateId('');
            setHtml('');
            setError('قوالب الطباعة غير متاحة؛ تحقق من الاتصال والصلاحية.');
        }
        finally {
            setTemplateLoading(false);
        }
    }, [currentDocType]);
    useEffect(() => {
        if (open) {
            setPhone(initialPhone || '');
            loadTemplates();
        }
        else {
            generationRef.current += 1;
            resetShare();
            setLoading(false);
            setHtml('');
            setError('');
            setOutputError('');
        }
    }, [open, initialPhone, loadTemplates, resetShare]);
    const loadWorkshop = useCallback(async () => {
        // يستخدم الأداة المساعدة الموحَّدة لجلب بيانات الورشة (اسم/شعار/ضريبي/ت.تجاري/…)
        // ⚠️ المفتاح الصحيح هو `logo` (Base64) وليس `logo_url`. تم تصحيحه هنا.
        return await loadWorkshopPrintInfo(async (path) => {
            const response = await api.get(path);
            return { ok: true, json: async () => response.data };
        });
    }, []);
    const printStabilityCss = `
    <style id="quick-print-stability-css">
      @page { size: A4; margin: 10mm; }
      @media print {
        .crow, .diag-b, .foot, .sign { display: table !important; width: 100% !important; table-layout: fixed !important; }
        .crow > div, .diag-b > div, .foot > div, .sign > div { display: table-cell !important; }
        .sheet { width: auto !important; }
        .pbtn { display: none !important; }
      }
    </style>`;
    const statusWatermarkBlock = (status) => {
        const raw = String(status || '').trim().toLowerCase();
        const label = ['draft', 'مسودة'].includes(raw)
            ? 'مسودة'
            : (['cancelled', 'canceled', 'ملغي', 'ملغاة'].includes(raw) ? 'ملغي' : '');
        if (!label)
            return '';
        return `<div class="qp-status-watermark" data-testid="quick-print-status-watermark">${label}</div><style>.qp-status-watermark{position:fixed;top:45%;left:50%;transform:translate(-50%,-50%) rotate(-24deg);font-size:110px;font-weight:900;color:rgba(190,18,60,.15);z-index:9999;pointer-events:none;white-space:nowrap}</style>`;
    };
    const wrapPrintableHtml = useCallback((rawHtml, status = '') => {
        if (!rawHtml)
            return '';
        const watermark = statusWatermarkBlock(status);
        if (/<html[\s>]/i.test(rawHtml)) {
            let full = rawHtml;
            if (!full.includes('quick-print-stability-css')) {
                if (/<head[\s>]/i.test(full)) {
                    full = full.replace(/<head([^>]*)>/i, `<head$1>${printStabilityCss}`);
                }
                else {
                    full = full.replace(/<html([^>]*)>/i, `<html$1><head>${printStabilityCss}</head>`);
                }
            }
            if (watermark && !full.includes('qp-status-watermark')) {
                full = /<\/body>/i.test(full) ? full.replace(/<\/body>/i, `${watermark}</body>`) : full + watermark;
            }
            return full;
        }
        return `<!doctype html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  ${printStabilityCss}
</head>
<body><main class="print-shell">${rawHtml}</main>${watermark}</body></html>`;
    }, []);
    const generateHtml = useCallback(async (builderOverride = null) => {
        const currentPayloadBuilder = builderOverride || payloadBuilderRef.current;
        if (!currentPayloadBuilder) {
            setLoading(false);
            setError('بيانات الطباعة غير جاهزة — أعد فتح المستند');
            return '';
        }
        const generation = ++generationRef.current;
        setLoading(true);
        setError('');
        setOutputError('');
        try {
            if (!selectedTemplate?.id)
                throw new Error('template_unavailable');
            const templateResponse = await runWithTimeout(api.post(`/document-templates/${selectedTemplate.id}/use`, { document_type: currentDocType }, { timeout: 15000 }), 15000);
            setTemplateSelectionReason(templateResponse.data?.selection_reason || '');
            const basePayload = await runWithTimeout(currentPayloadBuilder(), 15000);
            if (!basePayload) {
                throw new Error('missing-payload');
            }
            const workshop = { ...(await runWithTimeout(loadWorkshop(), 15000)), ...(basePayload.workshop || {}) };
            if (generation !== generationRef.current) return '';
            lastPayloadRef.current = { payload: basePayload, workshop };
            const rawHtml = renderDocumentTemplate(templateResponse.data?.content || '', basePayload, workshop);
            const nextHtml = wrapPrintableHtml(rawHtml, basePayload?.settings?.status || basePayload?.status || '');
            if (!nextHtml) {
                throw new Error('empty');
            }
            assertTemplateComplete(nextHtml);
            setHtml(nextHtml);
            return nextHtml;
        }
        catch (e) {
            if (generation !== generationRef.current) return '';
            setHtml('');
            const event = e?.code === 'template_incomplete' ? 'template_incomplete' : (e?.response ? 'template_load_failed' : 'template_render_failed');
            api.post('/document-templates/events', {
                event, template_id: selectedTemplate?.id, document_type: currentDocType,
                reason: e?.message || 'template_generation_failed', missing_variables: e?.variables || [],
            }).catch(() => null);
            const missing = e?.variables || findUnresolvedTemplateVariables(e?.response?.data?.detail?.content || '');
            const message = e?.message === 'invoice_not_issued_open_vehicle_profile'
                ? 'لا توجد فاتورة صادرة لهذه الزيارة. افتح ملف المركبة واختر «إصدار فاتورة مبيعات» أولًا.'
                : (e?.message === 'visit_final_customer_total_required_for_quickprint'
                ? 'يجب اعتماد الإجمالي النهائي للزيارة المحددة قبل طباعة الفاتورة.'
                : (e?.code ? e.message : (missing.length ? `القالب غير مكتمل. المتغيرات الناقصة: ${missing.join('، ')}` : (e?.message === 'timeout' ? 'انتهت مهلة إنشاء المعاينة' : (e?.response?.data?.detail?.message || 'تعذر إنشاء المعاينة بالقالب المختار')))));
            setError(message);
            return '';
        }
        finally {
            if (generation === generationRef.current) setLoading(false);
        }
    }, [currentDocType, loadWorkshop, runWithTimeout, selectedTemplate?.id, wrapPrintableHtml]);
    useEffect(() => {
        if (!open || templateLoading || !selectedTemplate?.id)
            return;
        let isActive = true;
        setHtml('');
        setError('');
        generateHtml(payloadBuilderRef.current).then((nextHtml) => {
            if (isActive && nextHtml)
                setHtml(nextHtml);
        });
        return () => { isActive = false; generationRef.current += 1; };
    }, [open, templateLoading, selectedTemplate?.id, generateHtml]);
    const getPrintableElement = async () => {
        const htmlContent = await generateHtml();
        if (!htmlContent)
            return null;
        return { ...createIsolatedPrintSurface(htmlContent, title), htmlContent };
    };
    const handlePrint = async () => {
        const htmlContent = await generateHtml();
        if (!htmlContent)
            return;
        try {
            printHtmlDocument(htmlContent, title);
        }
        catch (e) {
            setError(e?.message || 'تعذر فتح نافذة الطباعة');
        }
    };
    const handleDownloadPdf = async () => {
        const printable = await getPrintableElement();
        if (!printable)
            return;
        try {
            await downloadPDF(printable.element, `${title.replace(/\s+/g, '_')}.pdf`, {
                backgroundColor: '#ffffff',
                scale: 1.6,
            });
        }
        catch (e) {
            setOutputError(e?.message || 'تعذر إنشاء PDF بالقالب المختار');
        }
        finally {
            printable.cleanup();
        }
    };
    const handleWhatsApp = async () => {
        // Reload financial facts before preparing a message; never reuse stale payment totals.
        const htmlContent = await generateHtml();
        if (!htmlContent) return;
        const meta = lastPayloadRef.current || {};
        await prepareShare({
            docType: currentDocType, payload: meta.payload, workshop: meta.workshop,
            templateId: selectedTemplate?.id, templateVersion: selectedTemplate?.version,
            templateSelectionReason, phone: phone || meta.payload?.customer?.phone || '',
            fileBaseName: title.replace(/\s+/g, '_'), context: 'quick-print-dialog',
            getElement: async () => {
                return createIsolatedPrintSurface(htmlContent, title);
            },
        });
    };
    const runOutput = async (action, handler) => {
        if (outputBusyRef.current) return;
        outputBusyRef.current = true;
        setOutputAction(action);
        try { await handler(); }
        catch (e) { setError(e?.message || 'تعذر تجهيز المستند. حاول مرة أخرى.'); }
        finally { outputBusyRef.current = false; setOutputAction(''); }
    };
    if (!open) return null;
    if (share.stage !== 'idle') return <WhatsAppSharePreview share={share} onClose={resetShare} logEvent={logShareEvent}
        onPrepareAttachments={prepareAttachments} onOpenPrint={() => printHtmlDocument(html, title)} />;
    const actionsDisabled = loading || templateLoading || !html || Boolean(outputAction);
    const reasonLabels = { explicit_document_template:'القالب المحدد لهذا المستند', custom_default:'القالب الافتراضي للورشة', builtin_default:'قالب النظام الافتراضي' };
    return <DocumentOverlay onClose={onClose} labelledBy="quick-print-dialog-title" testId="quick-print-dialog">
        <header className="document-heading">
            <div><h3 id="quick-print-dialog-title" data-testid="quick-print-dialog-title">{title}</h3>
                <p data-testid="quick-print-dialog-description">{description}</p></div>
            <button type="button" onClick={onClose} data-testid="quick-print-action-cancel">إغلاق</button>
        </header>
        <div className="document-toolbar" data-testid="quick-print-current-template">
            <div className="document-template-name">{selectedTemplate?.name || 'جارٍ تحميل القالب…'}
                {templateSelectionReason && <small style={{display:'block'}}>{reasonLabels[templateSelectionReason] || 'قالب المستند المعتمد'}</small>}</div>
            <button type="button" disabled={actionsDisabled} onClick={() => setTemplateSheetOpen(v => !v)} data-testid="quick-print-change-template">تغيير القالب</button>
        </div>
        {templateSheetOpen && <div className="document-scroll" data-testid="quick-print-template-sheet">
            <button type="button" onClick={() => setTemplateSheetOpen(false)} data-testid="quick-print-template-sheet-close">إغلاق اختيار القالب</button>
            {filteredTemplates.map(tpl => <button type="button" key={tpl.id} data-testid={`quick-print-template-option-${tpl.id}`}
                onClick={() => {setSelectedTemplateId(tpl.id);setTemplateSheetOpen(false);}}>{tpl.name}</button>)}
        </div>}
        <div className="document-toolbar" aria-label="حجم المعاينة">
            <button type="button" onClick={() => setZoom(1)} aria-pressed={zoom === 1}>ملاءمة العرض</button>
            <button type="button" onClick={() => setZoom(v => Math.min(3,v+.5))} disabled={zoom >= 3} aria-label="تكبير المستند">＋</button>
            <button type="button" onClick={() => setZoom(v => Math.max(1,v-.5))} disabled={zoom <= 1} aria-label="تصغير المستند">−</button>
            <span>{Math.round(zoom*100)}٪</span>
        </div>
        {(loading || templateLoading || outputAction) && <p role="status" data-testid="quick-print-loading" style={{padding:'8px 16px'}}>
            {outputAction === 'whatsapp' ? 'جارٍ تحديث بيانات المستند وتجهيز معاينة واتساب…' : outputAction === 'pdf' ? 'جارٍ تجهيز PDF…' : 'جارٍ تجهيز المعاينة…'}
        </p>}
        {error && <div role="alert" className="document-error" data-testid="quick-print-error">{error}
            <button type="button" disabled={Boolean(outputAction)} onClick={() => selectedTemplate?.id ? generateHtml() : loadTemplates()} data-testid="quick-print-retry">إعادة المحاولة</button>
        </div>}
        {outputError && <div role="alert" className="document-error" data-testid="quick-print-output-error">{outputError}
            <button type="button" onClick={() => { try { printHtmlDocument(html,title); } catch (e) { setOutputError(e.message); } }}>فتح نسخة الطباعة / حفظ PDF</button>
        </div>}
        {html && !error && <DocumentViewport html={html} zoom={zoom} />}
        <footer className="document-actions" data-testid="quick-print-output-actions">
            <button type="button" className="document-primary" onClick={() => runOutput('whatsapp',handleWhatsApp)} disabled={actionsDisabled} data-testid="quick-print-action-whatsapp">واتساب</button>
            <button type="button" onClick={() => runOutput('pdf',handleDownloadPdf)} disabled={actionsDisabled} data-testid="quick-print-action-download-pdf">PDF</button>
            <button type="button" onClick={() => runOutput('print',handlePrint)} disabled={actionsDisabled} data-testid="quick-print-action-print">طباعة</button>
        </footer>
    </DocumentOverlay>;
};
export default QuickPrintDialog;
